import type { StationAccessType, StationOpeningHour } from '../../../api/station/stationApiTypes';
import type { OpeningHourEntry } from '../../../types/locations';

/** Room-station (1 object, no compartments) vs compartment-station (N positions + tech). */
export type StationType = 'compartment' | 'room';

/**
 * Which station the wizard creates. A `house` holds rooms and has no doors
 * (plan `room-houses-and-combining-rooms.md` §4 row 1): it is the station
 * module's variant, not a second wizard — the location step is shared, the
 * station step shrinks to name and description, and there are no objects and
 * no product, because a house's rooms are created one by one through room
 * creation (A1 h).
 */
export type WizardVariant = 'station' | 'house';

/** Are the locks already fitted, or does kolula still propose them? */
export type LockStatus = 'installed' | 'needed';

/** Is there a control unit already, does kolula supply one, or is it still open? */
export type ControlUnitStatus = 'existing' | 'kolula' | 'undecided';

/**
 * Wizard form state for the object & station creation flow (asset side —
 * architecture §9a). One flat object, updated immutably, mirroring the
 * AssetCreatePage convention. Numeric fields are strings while typed in the
 * input and parsed at validation/submit time.
 *
 * Spec: `docs/plans/object-and-station-creation-flow.md`.
 */
export interface StationCreationForm {
    // Step 1 — Location (retained geographic entity, architecture §4)
    locationMode: 'existing' | 'new';
    existingLocationId: string;
    /**
     * Name of a NEW location — and, for a new location, the station's name too.
     *
     * A new location holds exactly the station being created, so asking twice
     * produced two names for one thing (PO, 2026-09-07). When an EXISTING
     * location is picked the two separate again and `stationName` is asked on
     * step 2, because a location can legitimately hold several stations
     * (Segmenta: an office with a meeting room and a kolula locker in it).
     */
    newLocationName: string;
    newLocationStreet: string;
    newLocationHouseNumber: string;
    newLocationPostalCode: string;
    newLocationCity: string;
    /** Optional: the end-user room search filters by it (2026-10-19). */
    newLocationDistrict: string;
    /** The new location's map pin — optional, both or neither (PO, 2026-10-16). */
    newLocationGeoLat: number | null;
    newLocationGeoLng: number | null;
    /** Opening hours of a NEW location. Prefilled 24/7 and editable: a weekday
     *  with no row is CLOSED, so "no answer" is not a neutral default. */
    newLocationOpeningHours: OpeningHourEntry[];
    /**
     * Whether the opening-hours editor currently holds a complete set of rows.
     *
     * `OpeningHoursSection` silently DROPS a half-typed row (`from` filled,
     * `to` empty) when it converts its draft to entries, so without this the
     * wizard would accept "Monday 8:00–" and create a station closed on
     * Mondays while the operator believes otherwise. The reference screen
     * (`AssetCreatePage`) wires the same callback.
     */
    newLocationOpeningHoursValid: boolean;

    // Step 2 — Station (= depot)
    stationType: StationType;
    /** Asked only when placing the station at an EXISTING location. */
    stationName: string;
    /** door_count — number of numbered positions (compartment-station only). */
    positionCount: string;
    /** buffer_doors_count — held-back / technical positions. */
    bufferCount: string;
    /** Physical cabinet/hardware type. Not yet surfaced in the UI — TODO(#2134)
     *  add a field before submit is wired; until then the backend defaults it. */
    containerType: string;
    /**
     * Access is an orthogonal add-on declared inventory-style (PRD-001,
     * `door_mechanism`). `none` = numbered/manual; `raspberry_pi` = own IoT
     * hardware; `dormakaba` = manufacturer API door lock. Ops sets the real
     * mechanism at connection — the wizard only captures the declaration.
     */
    accessType: StationAccessType;
    /** Whether the locks are already fitted, or kolula should propose them. */
    lockStatus: LockStatus | '';
    /** Free-text: which lock is installed — only asked when it already is. */
    lockDeclaration: string;
    /** Whether a control unit exists, kolula supplies one, or it is still open. */
    controlUnitStatus: ControlUnitStatus | '';
    /** Free-text: installed hardware note (Pi model, relay, router, serials). */
    installedHardware: string;
    description: string;

    // Step 3 — Objects (bulk)
    objectNaming: 'auto' | 'manual';
    /** Prefix for auto-naming, e.g. "Compartment" → "Compartment 1..N". */
    objectNamePrefix: string;
    /** Manual per-object names (length = bookable count). */
    objectNames: string[];
    /** Optional per-object details (manual mode), indexed like `objectNames`:
     *  the unit's serial/identifier, exact volume (cm³), and a unit-specific note.
     *  All object-level and optional (architecture §2c). */
    objectSerials: string[];
    objectVolumes: string[];
    objectNotes: string[];
    /** Volume and note applied to EVERY object in auto-naming mode, where the
     *  objects are alike by definition and are described once rather than in N
     *  identical cards. Manual mode keeps the per-unit fields. */
    sharedObjectVolume: string;
    sharedObjectNote: string;

    // Step 4 — Link to Product (junction). Mode is always `any_of`:
    // `exclusive` is a second Product over the same objects, not a choice made
    // here (see LinkProductStep).
    /**
     * How the station's positions divide between Products (#2280).
     *
     * A real cabinet is 3× L, 2× S, 3× M and size is product-defining
     * (`product-and-asset-model.md` §2b), so one station is several Products.
     * One row is the ordinary case and the step looks like a single picker
     * until "add another product" is pressed.
     *
     * Rows carve the positions the objects step already named into CONSECUTIVE
     * blocks — 5× L takes 1–5, 5× M takes 6–10 — rather than letting the
     * operator assign position by position. Per-position assignment already
     * exists on the station page (the position list re-links a ticked set), and
     * a second place answering "which product is this compartment?" is exactly
     * the duplication CLAUDE.md's one-rule-one-place forbids.
     */
    productRows: StationProductRow[];
}

/** One Product and how many of the station's positions it takes. */
export interface StationProductRow {
    productId: string;
    /** Object count, as typed. Parsed at validation/submit like every count here. */
    count: string;
}

/** The seven weekdays, open around the clock — the wizard's prefill. */
export const ALL_WEEKDAYS: StationOpeningHour['weekday'][] = [
    'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',
];

export const makeAlwaysOpenHours = (): OpeningHourEntry[] =>
    ALL_WEEKDAYS.map((weekday) => ({ weekday, times: { fromHour: 0, toHour: 24 } }));

export const makeInitialStationForm = (): StationCreationForm => ({
    // A new location first (PO, 2026-10-08): it is where the address and
    // opening hours are entered, so the operator sees what to fill in;
    // picking an existing one is the alternative.
    locationMode: 'new',
    existingLocationId: '',
    newLocationName: '',
    newLocationStreet: '',
    newLocationHouseNumber: '',
    newLocationPostalCode: '',
    newLocationCity: '',
    newLocationDistrict: '',
    newLocationGeoLat: null,
    newLocationGeoLng: null,
    newLocationOpeningHours: makeAlwaysOpenHours(),
    newLocationOpeningHoursValid: true,
    stationType: 'compartment',
    stationName: '',
    positionCount: '',
    bufferCount: '0',
    containerType: 'cp1',
    accessType: 'none',
    lockStatus: '',
    lockDeclaration: '',
    controlUnitStatus: '',
    installedHardware: '',
    description: '',
    objectNaming: 'auto',
    objectNamePrefix: '',
    objectNames: [],
    objectSerials: [],
    objectVolumes: [],
    objectNotes: [],
    sharedObjectVolume: '',
    sharedObjectNote: '',
    productRows: [{ productId: '', count: '' }],
});

/**
 * The station's name. One name per physical thing: a new location is created
 * for exactly this station, so its name is the station's; an existing location
 * may already hold others, so the station carries its own.
 */
export const stationNameOf = (form: StationCreationForm): string => (
    form.locationMode === 'new' ? form.newLocationName.trim() : form.stationName.trim()
);

/** Parse a non-negative integer field; returns null when not a clean integer. */
export const parseCount = (value: string): number | null => {
    if (!/^\d+$/.test(value.trim())) return null;
    return Number.parseInt(value.trim(), 10);
};

/**
 * Bookable object count. A room-station is exactly one object (itself). A
 * compartment-station is numbered positions minus held-back/technical ones.
 * Returns null when the position count is not yet a valid integer.
 */
export const bookableObjectCount = (form: StationCreationForm): number | null => {
    if (form.stationType === 'room') return 1;
    const positions = parseCount(form.positionCount);
    const buffer = parseCount(form.bufferCount) ?? 0;
    if (positions === null) return null;
    return Math.max(positions - buffer, 0);
};

/** One Product and the resolved number of positions it takes. */
export interface ResolvedProductRow {
    productId: string;
    count: number;
}

/**
 * The product rows with their counts resolved, or `null` when they do not add
 * up to the station's bookable objects.
 *
 * **A single row means "all of them".** The count is then implied by the
 * position count from step 2, so the ordinary single-product station is chosen
 * exactly as it was before #2280 — pick a Product and continue — and nobody has
 * to type a number they already typed. Only once a second row exists does the
 * split have to be stated, because only then is there something to state.
 *
 * Returns null rather than a best effort: a row list that does not total the
 * bookable count would silently leave positions empty or overflow the cabinet,
 * and the step's validation turns this null into a message.
 */
export const resolvedProductRows = (
    form: StationCreationForm,
): ResolvedProductRow[] | null => {
    const bookable = bookableObjectCount(form);
    if (bookable === null || bookable < 1) return null;

    const rows = form.productRows;
    if (rows.length === 0) return null;
    if (rows.some((row) => !row.productId.trim())) return null;

    if (rows.length === 1) {
        const only = rows[0];
        if (!only) return null;
        return [{ productId: only.productId.trim(), count: bookable }];
    }

    const resolved: ResolvedProductRow[] = [];
    for (const row of rows) {
        const count = parseCount(row.count);
        if (count === null || count < 1) return null;
        resolved.push({ productId: row.productId.trim(), count });
    }
    const total = resolved.reduce((sum, row) => sum + row.count, 0);
    return total === bookable ? resolved : null;
};

/** What the rows currently add up to — for the step's running hint. */
export const productRowsTotal = (form: StationCreationForm): number => form.productRows
    .reduce((sum, row) => sum + (parseCount(row.count) ?? 0), 0);
